// Persistence for the password generator's NON-SECRET ui preferences — length,
// which character classes are enabled, and the exclude-similar flag. Stored in
// puter.kv (the same per-user key-value store the favicon worker uses), so the
// settings follow the user across devices. Outside a Puter session puter.kv is
// absent; load/save then degrade to an in-memory session cache, so the
// generator still remembers within a page load. The generated password itself
// is NEVER persisted here — only the knobs.

const KV_KEY = 'pwvault:generatorOpts';

export const DEFAULT_OPTS = {
  length: 20,
  upper: true,
  lower: true,
  digits: true,
  symbols: true,
  excludeSimilar: false,
};

let cache = null; // session memory; also the offline fallback

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

// Coerce arbitrary input into a complete, valid opts object. Length is clamped
// to the slider's 8..64 range; classes/flags become booleans. An all-off class
// set (which would make an empty pool) is repaired to the defaults so
// generation can never fail.
export function sanitizeOpts(o) {
  const n = Math.round(Number(o?.length));
  const out = {
    length: Number.isFinite(n) ? clamp(n, 8, 64) : DEFAULT_OPTS.length,
    upper: !!o?.upper,
    lower: !!o?.lower,
    digits: !!o?.digits,
    symbols: !!o?.symbols,
    excludeSimilar: !!o?.excludeSimilar,
  };
  if (!out.upper && !out.lower && !out.digits && !out.symbols) {
    out.upper = DEFAULT_OPTS.upper;
    out.lower = DEFAULT_OPTS.lower;
    out.digits = DEFAULT_OPTS.digits;
    out.symbols = DEFAULT_OPTS.symbols;
  }
  return out;
}

// Synchronous best guess for an instant modal open: the session cache if we
// have one, else the defaults. May be a tab-local snapshot (another tab's
// saveOpts won't update it until the next loadOpts) — fine for a UI preference.
export function cachedOpts() {
  return cache ? { ...cache } : { ...DEFAULT_OPTS };
}

// Load the saved opts from puter.kv, falling back to the session cache /
// defaults. Populates the session cache on success. Never throws.
export async function loadOpts() {
  const p = globalThis.puter;
  if (typeof p?.kv?.get === 'function') {
    try {
      const raw = await p.kv.get(KV_KEY);
      if (raw) {
        cache = sanitizeOpts(typeof raw === 'string' ? JSON.parse(raw) : raw);
        return { ...cache };
      }
    } catch { /* ignore — fall back below */ }
  }
  return cachedOpts();
}

// Persist opts: always update the session cache; best-effort write to puter.kv.
// Never throws. Returns the sanitized opts actually stored.
export function saveOpts(opts) {
  cache = sanitizeOpts(opts);
  const p = globalThis.puter;
  if (typeof p?.kv?.set === 'function') {
    try {
      const r = p.kv.set(KV_KEY, JSON.stringify(cache));
      if (r && typeof r.catch === 'function') r.catch(() => {});
    } catch { /* best-effort */ }
  }
  return { ...cache };
}
